import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core';
import clevertap from 'clevertap-web-sdk';
import { Mixpanel } from 'mixpanel-browser';
import { v4 as uuidv4 } from 'uuid';
import { NavigationMapEntry, TrackingAction, TrackingProperty } from '@rapid-recovery-agency-inc/insightt-shared';
import { TrackedUser } from './utils';
import { lsGetEnvVariables } from '../../modules/environment/environment-utils';
import { store, type AppStore } from '../../redux/store';
import storage from '../../helpers/storage';
import { getCurrentPositionAsync } from '../location/services';

type ClevertapInstance = typeof clevertap;

/**
 * The `Analytics` class provides methods for initializing and configuring the Mixpanel and Clevertap analytics providers.
 */
class Analytics {
  private mixpanel: Mixpanel | null = null;
  private readonly mixpanelToken: string | null;
  private readonly clevertapToken: string | null;
  private readonly clevertapAccount: string | null;
  private unsubscribe?: () => void;
  private store: AppStore;
  private isIdentified: boolean = false;
  private clevertap: ClevertapInstance | null = null;

  constructor(
    mixpanelToken: string | null,
    clevertapToken: string | null,
    clevertapAccount: string | null,
    store: AppStore,
  ) {
    this.mixpanelToken = mixpanelToken;
    this.clevertapToken = clevertapToken;
    this.clevertapAccount = clevertapAccount;
    this.store = store;

    if (this.mixpanelToken) {
      mixpanel.init(
        this.mixpanelToken,
        {
          persistence: 'localStorage',
          loaded: (mixpanel: Mixpanel) => {
            this.mixpanel = mixpanel;
          },
          // TEST: Info to see if page tracking is still working
          // track_pageview: true,
          ignore_dnt: true, // The actions are not being sent if DNT is enabled
        },
        'Insightt - Web',
      );
    }
    this.checkAnonymousIdentify();

    if (this.clevertapAccount && this.clevertapToken) {
      clevertap.init(this.clevertapAccount, 'eu1', undefined, this.clevertapToken);
      clevertap.setLogLevel(3);
      clevertap.notifications.push({
        titleText: 'Would you like to receive Push Notifications?',
        bodyText: 'We promise to only send you relevant content and give you updates on your transactions',
        okButtonText: 'Sign me up!',
        rejectButtonText: 'No thanks',
        okButtonColor: '#F28046',
        askAgainTimeInSeconds: 5,
        serviceWorkerPath: '/clevertap_sw.js', // path to your custom service worker file
      });
      this.clevertap = clevertap;
    }
  }

  getDoNotTrack(): boolean {
    // Handle various browser implementations
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let dnt = navigator.doNotTrack ?? (window as any).doNotTrack ?? (navigator as any).msDoNotTrack;

    if (!dnt || dnt === 'unspecified') {
      // Problem using "Do Not Track" on Firefox, returning "unspecified"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dnt = (navigator as any).globalPrivacyControl ?? null;
    }

    // Normalize to lowercase string
    const val = String(dnt).toLowerCase();

    const truthy = new Set(['1', 'yes', 'true']);
    const falsy = new Set(['0', 'no', 'false']);

    if (truthy.has(val)) return true;
    if (falsy.has(val)) return false;

    // Unknown value, we assume there is no DNT
    return false;
  }

  private isClevertapEnabled(): boolean {
    const state = this.store.getState();
    const flags = state.system.session.featureFlagsMap;
    return flags.WEB_CLEVERTAP || false;
  }

  public dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
  }

  // identify(id: string) {
  //   return this.mixpanel.identify(id);
  // }

  anonymousIdentify() {
    let anonymousUserId = storage.get('anonymous_user_id');
    const anonymousUserIdExists = anonymousUserId !== null;
    if (!anonymousUserIdExists) {
      anonymousUserId = uuidv4();
      storage.set('anonymous_user_id', anonymousUserId);
    }
    return anonymousUserId!;
  }

  checkAnonymousIdentify() {
    const user = storage.get('user') ? JSON.parse(storage.get('user')!) : undefined;
    if (!user) {
      return;
    }

    if (!this.mixpanel) {
      return;
    }
    // Check if user has activated/deactivated anonymous track
    if (this.getDoNotTrack() && this.mixpanel.get_distinct_id() != this.anonymousIdentify()) {
      this.mixpanel.reset();
      this.mixpanel.identify(this.anonymousIdentify());
    } else if (!this.getDoNotTrack() && this.mixpanel.get_distinct_id() != String(user.id)) {
      this.mixpanel.reset();
      this.mixpanel.identify(String(user.id));
    }
  }

  identify({
    id,
    email,
    firstName,
    lastName,
    role,
    roleId,
    company,
    companyId,
    phoneNumber,
    tier,
    deviceType,
    type,
    subscription,
  }: TrackedUser) {
    if (!this.mixpanel) {
      return;
    }

    if (this.getDoNotTrack()) {
      this.isIdentified = true;
      this.mixpanel.identify(String(this.anonymousIdentify()));
      return;
    }
    this.isIdentified = true;
    this.mixpanel.identify(String(id));
    this.mixpanel.people.set({
      $id: id,
      $email: email,
      $first_name: firstName,
      $last_name: lastName,
      $role: role,
      $role_id: roleId,
      $type: type,
      $company: company,
      $company_id: companyId,
      $phone_number: phoneNumber,
      $plan: subscription.plan,
      $tier: tier,
      $device_type: deviceType,
      // $registration_method: registrationMethod,
    });

    if (this.isClevertapEnabled()) {
      const profile: Record<string, string | number> = {
        Identity: String(id),
      };

      profile.Email = email;
      if (firstName || lastName) profile.Name = `${firstName || ''} ${lastName}`.trim();
      if (phoneNumber) profile.Phone = phoneNumber;
      if (role) profile.Role = role;
      if (roleId) profile.RoleId = roleId;
      if (company) profile.Company = company;
      if (companyId) profile.CompanyId = companyId;

      this.clevertap?.onUserLogin.push({ Site: profile });
    }
  }

  track(event: string, eventObject?: TrackingProperty) {
    if (!this.isIdentified) {
      return;
    }
    if (this.isClevertapEnabled() && this.clevertap) {
      this.clevertap.event.push(event);
    }
    if (event != TrackingAction.USER_SIGNUP_INVITED)
      // We are creating user, so we need to send the user id, this tracks only the created user's information
      this.checkAnonymousIdentify();
    if (this.mixpanel) this.mixpanel.track(event, eventObject);
  }

  reset() {
    if (this.mixpanel) {
      this.mixpanel.reset();
    }
    this.dropQueuedBatches();
  }

  /**
   * `mixpanel.reset()` leaves the unsent request batches (`__mpq_<token>_ev/_pp/_gr`) in
   * localStorage, and the profile batch carries the signed-out user's email and name (FR-022,
   * FR-025). Drop them; events the user produced but Mixpanel had not sent yet are lost.
   */
  private dropQueuedBatches() {
    if (!this.mixpanelToken) return;
    const prefix = `__mpq_${this.mixpanelToken}_`;
    Object.keys(localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => localStorage.removeItem(key));
  }
}

let instance: Analytics | null = null;

export const analytics = (): Analytics => {
  if (instance) return instance;
  const { MIXPANEL_KEY, CLEVERTAP_ACCOUNT, CLEVERTAP_TOKEN } = lsGetEnvVariables().envObj;
  instance = new Analytics(MIXPANEL_KEY ?? null, CLEVERTAP_TOKEN, CLEVERTAP_ACCOUNT, store);
  return instance;
};

/**
 * Dedicated delivery for authentication outcome events (FR-025, FR-026, FR-046; T073). Unlike
 * {@link Analytics#track}, it works before sign-in and after logout, never identifies a user, adds no
 * geolocation or module/view properties, and strips the URL and referrer properties Mixpanel adds by
 * default, so an event carries only the fields the caller passes. A separate, never-identified
 * Mixpanel instance keeps it apart from the signed-in user's analytics identity.
 */
const AUTH_TELEMETRY_BLOCKED_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$referring_domain',
  '$initial_referrer',
  '$initial_referring_domain',
  '$search_engine',
  'mp_keyword',
];

let authTelemetryClient: Mixpanel | null | undefined;

const getAuthTelemetryClient = (): Mixpanel | null => {
  if (authTelemetryClient === undefined) {
    const { MIXPANEL_KEY } = lsGetEnvVariables().envObj;
    authTelemetryClient = MIXPANEL_KEY
      ? (mixpanel.init(
          MIXPANEL_KEY,
          {
            disable_persistence: true,
            ip: false,
            property_blacklist: AUTH_TELEMETRY_BLOCKED_PROPERTIES,
            ignore_dnt: true,
          },
          'insightt-auth-telemetry',
        ) as Mixpanel)
      : null;
  }
  return authTelemetryClient;
};

/**
 * Sends one authentication telemetry event with exactly `properties`. Uses `sendBeacon`, so an
 * event sent right before a sign-out navigation still reaches the transport.
 */
export const trackAuthenticationTelemetry = (
  event: string,
  properties: Readonly<Record<string, string | number>>,
) => {
  getAuthTelemetryClient()?.track(event, { ...properties }, { transport: 'sendBeacon', send_immediately: true });
};

export const identify = (user: TrackedUser) => {
  analytics().identify(user);
};

/**
 * Resets the current analytics identity (T037; FR-021; spec.md US5 Acceptance Scenario 4 "no
 * ... analytics identity ... from the prior user is observable"). Wraps the previously
 * internal-only `Analytics#reset()` as a standalone export, mirroring `identify()`'s shape, so
 * every session-cleanup trigger point (`system/utils.ts`, `shared/utils.ts`) can drop the
 * previous user's Mixpanel identity without reaching into the class instance directly.
 *
 * Mixpanel only: this SDK's `Analytics` class has no Clevertap analog to `mixpanel.reset()` —
 * Clevertap's own model instead re-identifies on the next `onUserLogin.push()` call (see
 * `identify()` above), and no `logout`/`reset`-shaped export exists anywhere in
 * `clevertap-web-sdk` (confirmed against the installed package's bundled source, which defines no
 * such method). There is deliberately nothing to call here for Clevertap.
 */
export const resetAnalyticsIdentity = () => {
  analytics().reset();
};

const getCurrentCoordinates = (): Promise<GeolocationPosition | null> => {
  return new Promise((resolve: (value: GeolocationPosition | null) => void, reject: PositionErrorCallback) => {
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((permission) => {
        if (permission.state !== 'granted') {
          resolve(null);
          return;
        }
        getCurrentPositionAsync({
          onResolve: resolve,
          onReject: reject,
          options: {
            enableHighAccuracy: true,
            maximumAge: Infinity,
            timeout: 10000,
          },
        });
      })
      .catch(() => {
        resolve(null);
      });
  });
};

export function createAnalyticEvent(
  event: TrackingAction,
  navigationEntry?: NavigationMapEntry,
): (extra?: TrackingProperty) => void;

export function createAnalyticEvent<T>(
  event: TrackingAction,
  navigationEntry?: NavigationMapEntry,
  mapProperties?: (entry: T) => TrackingProperty,
): <U extends T>(input: U, extra?: TrackingProperty) => void;

export function createAnalyticEvent<T>(
  event: TrackingAction,
  navigationEntry?: NavigationMapEntry,
  mapProperties?: (entry: T) => TrackingProperty,
) {
  return async <U extends T>(input: U, extra?: TrackingProperty) => {
    const properties = mapProperties ? mapProperties(input) : input;
    // Attempts to retrieve the user's exact coordinates without interrupting tracking if location access fails
    let coordinates: GeolocationPosition | null = null;
    try {
      coordinates = await getCurrentCoordinates();
    } catch {
      // Having undefined coordinates will not send exact location, but will allow Mixpanel to track by the user's IP address
    }
    analytics().track(event, {
      ...(navigationEntry && {
        $navigation_entry_type: navigationEntry.type,
        $module: navigationEntry.module,
        $feature: 'feature' in navigationEntry ? navigationEntry.feature : undefined,
        $view: 'view' in navigationEntry ? navigationEntry.view : undefined,
        $sub_view: 'subView' in navigationEntry ? navigationEntry.subView : undefined,
      }),
      ...(analytics().getDoNotTrack()
        ? {
            $anonymous_id: analytics().anonymousIdentify(),
          }
        : {
            ...properties,
            ...(extra ?? {}),
          }),
      ...(coordinates && {
        $latitude: coordinates.coords.latitude,
        $longitude: coordinates.coords.longitude,
      }),
    });
  };
}
